/* eslint-disable no-inline-styles/no-inline-styles */
import type { ISpotScore, SpotScoreDetailsAttributes } from '@/features/spot-score/spot-score-type';

import React from 'react';

import { HtmlTextWrapper } from '@/core/features';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import IconImage from '@/core/features/image-v2/icon-image';
import color from '@/core/features/styles/color';

interface SpotScoreOverviewProps {
    isVisible?: boolean;
    spotScoreDetails: NonNullable<ISpotScore['details']>;
    spotScoreText: string;
}

export default function SpotScoreOverview({ isVisible, spotScoreDetails, spotScoreText }: SpotScoreOverviewProps) {
    const { attributes, description, scoreLegends, subTitle, title } = spotScoreDetails;

    const [showDetails, setShowDetails] = React.useState(false);

    const { isDesktopView } = useDeviceoutput();

    return (
        <div className={'flex-column'}>
            {!isDesktopView && (
                <div
                    className={'margin-bottom-10 flex border-radius-5'}
                    style={{ backgroundColor: color('athensGray') }}
                >
                    <div
                        className={'flex-center padding-10 font-size-14 bold flex-1 border-radius-5'}
                        onClick={() => setShowDetails(false)}
                        style={{
                            backgroundColor: showDetails ? color('white') : undefined,
                            border: `solid 1px ${color('athensGray')}`,
                        }}
                    >
                        Übersicht
                    </div>
                    <div
                        className={'flex-center padding-10 font-size-14 bold flex-1 border-radius-5'}
                        onClick={() => setShowDetails(true)}
                        style={{
                            backgroundColor: !showDetails ? color('white') : undefined,
                            border: `solid 1px ${color('athensGray')}`,
                        }}
                    >
                        Details
                    </div>
                </div>
            )}
            <div className={'margin-bottom-15'}>
                <div className={'flex-column margin-bottom-15'}>
                    <div className={'line-clamp-1 font-size-18 bold'}>{title}</div>
                    <div className={'line-clamp-1 font-size-18'}>{subTitle}</div>
                </div>
                <div className={'flex align-center'}>
                    <div
                        className={'flex-center margin-right-8 font-size-14 text-white bold bg-check24Blue'}
                        style={{
                            borderRadius: '5px 5px 0px 5px',
                            height: '32px',
                            width: '32px',
                        }}
                    >
                        {spotScoreText}
                    </div>
                    <div className={'ellipsis font-size-14 bold'}>
                        <HtmlTextWrapper htmlText={spotScoreDetails.htmlScoreDescriptionVerbose} />
                    </div>
                </div>
            </div>
            <div>
                {attributes.map((attribute, index) => {
                    {
                        return (
                            <SpotScoreBar
                                isVisible={isVisible ?? true}
                                key={attribute.name + index}
                                showDetails={showDetails}
                                {...attribute}
                            />
                        );
                    }
                })}
                {scoreLegends.map((scoreLegend, index) => {
                    return (
                        <div
                            className={'margin-bottom-5 font-size-14 flex align-center'}
                            data-nosnippet={true}
                            key={`score-legend-${index}`}
                        >
                            <IconImage
                                className={'margin-right-8'}
                                height={10}
                                rotate={scoreLegend.rotate}
                                url={scoreLegend.iconUrl}
                                width={10}
                            />
                            <span>{scoreLegend.descriptionText}</span>
                        </div>
                    );
                })}

                <div
                    className={'margin-bottom-10 font-size-12'}
                    data-nosnippet={true}
                >
                    {description}
                </div>

                {isDesktopView && (
                    <div
                        className={'justify-end width-100 font-size-14 flex pointer'}
                        style={{ color: color('lochmara') }}
                    >
                        {showDetails ? (
                            <a onClick={() => setShowDetails(false)}>Bewertungsdetails ausblenden</a>
                        ) : (
                            <a onClick={() => setShowDetails(true)}>Bewertungsdetails einblenden</a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

type SpotScoreBarProps = SpotScoreDetailsAttributes & {
    isVisible: boolean;
    showDetails: boolean;
};

function SpotScoreBar({
    color: barColor,
    icon,
    isVisible,
    name,
    score,
    scoreDescription,
    showDetails,
}: SpotScoreBarProps) {
    const [animatedScore, setAnimatedScore] = React.useState(0);
    React.useEffect(() => {
        if (!isVisible) {
            return;
        }

        setAnimatedScore(score);

        return () => {
            setAnimatedScore(0);
        };
    }, [score, isVisible]);

    return (
        <div className={'flex-column margin-bottom-10'}>
            <div className={'margin-bottom-5 line-height-18 font-size-16 bold flex align-center justify-space-between'}>
                <div className={'flex align-center'}>
                    <span className={'margin-right-5'}>{name}</span>
                    {icon && (
                        <IconImage
                            height={10}
                            rotate={icon.rotate}
                            url={icon.iconUrl}
                            width={10}
                        />
                    )}
                </div>
                {score.toFixed(1)}
            </div>
            <div
                className={`width-100 ${!showDetails && 'margin-bottom-5'}`}
                style={{ backgroundColor: color('athensGray'), borderRadius: '8px', height: '5px' }}
            >
                <div
                    style={{
                        backgroundColor: barColor,
                        borderRadius: '8px',
                        height: '5px',
                        transition: 'width 800ms ease-in-out',
                        width: `${animatedScore * 10}%`,
                    }}
                />
            </div>
            {showDetails && <span className={'margin-top-5 font-size-14'}>{scoreDescription}</span>}
        </div>
    );
}
